/**
 * HostCleaningAppPage — Spin-off WebApp Host Pulizie.
 *
 * UI semplificata stile PWA, sempre loggata (token persistente in localStorage
 * grazie a ProtectedRoute + axios interceptor). Pensata per uso mobile in
 * mobilità: l'host scorre rapidamente i turni in arrivo, vede chi ha completato
 * e legge le segnalazioni più recenti dello staff.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  CheckCircle,
  Sparkle,
  ArrowLeft,
  Warning,
  Bed,
  House,
  Camera,
  ArrowsClockwise,
  Bell,
  X,
  Plus,
  Gear,
} from '@phosphor-icons/react';
import ConfigureTaskDialog from '../components/ConfigureTaskDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
    return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
}

function dayBucket(checkOut) {
  if (!checkOut) return 'altri';
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  if (checkOut < today) return 'in_ritardo';
  if (checkOut === today) return 'oggi';
  if (checkOut === tomorrow) return 'domani';
  return 'prossimi';
}

export default function HostCleaningAppPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);
  const [tab, setTab] = useState('today'); // today | upcoming | reports
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualDraft, setManualDraft] = useState({ property_id: '', room_id: '', check_in: '', check_out: '', num_guests: 2, notes: '' });
  const [creating, setCreating] = useState(false);
  const [configureTask, setConfigureTask] = useState(null);
  const recentDoneRef = useRef(new Set());

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [tRes, pRes, rRes, sRes, repRes] = await Promise.all([
        axios.get(`${API}/cleaning/tasks`),
        axios.get(`${API}/properties`),
        axios.get(`${API}/cleaning/rooms`),
        axios.get(`${API}/cleaning/stats`),
        axios.get(`${API}/cleaning/reports/recent?limit=10`).catch(() => ({ data: [] })),
      ]);
      setTasks(tRes.data || []);
      setProperties(pRes.data || []);
      setRooms(rRes.data || []);
      setStats(sRes.data || null);
      setReports(repRes.data || []);
    } catch (e) {
      if (!silent) toast.error('Errore di caricamento');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // SSE realtime
  useEffect(() => {
    if (!user?.id) return;
    const es = new EventSource(`${API}/cleaning/stream`, { withCredentials: true });
    es.addEventListener('task_done', (ev) => {
      try {
        const d = JSON.parse(ev.data);
        recentDoneRef.current.add(d.task_id);
        setTasks(prev => prev.map(t => t.id === d.task_id ? { ...t, status: 'fatto', completed_at: d.completed_at } : t));
        toast.success('✓ Pulizia completata', { duration: 4500 });
        setTimeout(() => recentDoneRef.current.delete(d.task_id), 6000);
      } catch {}
    });
    es.addEventListener('task_report', () => {
      toast.warning('Nuova segnalazione dallo staff', { duration: 5000 });
      fetchAll(true);
    });
    es.onerror = () => {};
    return () => es.close();
  }, [user?.id, fetchAll]);

  const propById = (id) => properties.find(p => (p.id || p._id) === id);
  const roomById = (id) => rooms.find(r => r.id === id);

  const propsWithCleaning = properties.filter(p => p.cleaning_type);

  const onCreateManual = async () => {
    if (!manualDraft.property_id || !manualDraft.check_in || !manualDraft.check_out) {
      toast.error('Compila struttura e date');
      return;
    }
    const prop = propById(manualDraft.property_id);
    if (prop?.cleaning_type === 'stanze' && !manualDraft.room_id) {
      toast.error('Seleziona la stanza');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/cleaning/tasks`, manualDraft);
      toast.success('Pulizia aggiunta');
      setShowManualDialog(false);
      setManualDraft({ property_id: '', room_id: '', check_in: '', check_out: '', num_guests: 2, notes: '' });
      fetchAll(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setCreating(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  // STRICT FILTER:
  //  - "today" tab mostra SOLO turni con check_out ∈ {oggi, domani}
  //  - i passati (check_out < oggi) sono visibili SOLO nel dropdown <details>
  const todayTasks = tasks.filter(t => (t.check_out === today || t.check_out === tomorrow) && t.status === 'da_fare');
  const upcomingTasks = tasks.filter(t => t.check_out > tomorrow && t.status === 'da_fare').slice(0, 30);
  const pastTasks = tasks.filter(t => t.check_out < today && t.status === 'da_fare');
  const doneTodayCount = tasks.filter(t => t.status === 'fatto' && t.check_out === today).length;

  // Build tabs data — più "in ritardo" qui (sta nel dropdown)
  const groupedToday = {
    oggi: todayTasks.filter(t => t.check_out === today),
    domani: todayTasks.filter(t => t.check_out === tomorrow),
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-24" data-testid="host-cleaning-app">
      {/* App-style header */}
      <header className="sticky top-0 z-30 bg-white border-b border-zinc-200 shadow-sm">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-900 text-xs font-bold uppercase tracking-wider"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={14} weight="bold" /> Dashboard
          </button>
          <div className="flex items-center gap-1.5">
            <Sparkle size={16} weight="fill" className="text-emerald-600" />
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-900">Pulizie · WebApp</p>
          </div>
          <button
            onClick={() => fetchAll()}
            disabled={refreshing}
            className="p-1.5 text-zinc-500 hover:text-zinc-900 disabled:opacity-50"
            data-testid="refresh-btn"
            aria-label="Aggiorna"
          >
            <ArrowsClockwise size={16} weight="bold" className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </header>

      {/* KPI strip */}
      <section className="max-w-md mx-auto px-4 pt-4">
        <div className="grid grid-cols-3 gap-2" data-testid="kpi-strip">
          <KPI label="Oggi" value={todayTasks.length} sub={`${doneTodayCount} fatte`} color="emerald" />
          <KPI label="Prossimi" value={upcomingTasks.length} sub="da fare" color="blue" />
          <KPI label="Segnalazioni" value={stats?.reports_total ?? 0} sub="totali" color="rose" />
        </div>
      </section>

      {/* Tabs */}
      <nav className="max-w-md mx-auto px-4 pt-4">
        <div className="flex gap-1 bg-white border border-zinc-200 p-1" role="tablist" data-testid="tabs">
          {[
            { id: 'today', label: 'Oggi · Domani', count: todayTasks.length },
            { id: 'upcoming', label: 'Prossimi', count: upcomingTasks.length },
            { id: 'reports', label: 'Report', count: reports.length, icon: Bell },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              role="tab"
              aria-selected={tab === t.id}
              className={`flex-1 py-2 px-2 text-[11px] font-black uppercase tracking-wider transition-colors ${tab === t.id ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:text-zinc-900'}`}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
              {t.count > 0 && (
                <span className={`ml-1 px-1 ${tab === t.id ? 'bg-white/20' : 'bg-zinc-100'}`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Body */}
      <main className="max-w-md mx-auto px-4 pt-4">
        {loading && (
          <div className="text-center py-10 text-zinc-400 text-sm">Caricamento…</div>
        )}

        {!loading && tab === 'today' && (
          <>
            {todayTasks.length === 0 && pastTasks.length === 0 && (
              <EmptyState
                icon={CheckCircle}
                title="Tutto pulito!"
                body="Nessun turno in scadenza oggi o domani. Lo staff ha già completato tutto."
              />
            )}
            {groupedToday.oggi.length > 0 && (
              <Section title="Oggi" tone="emerald" count={groupedToday.oggi.length}>
                {groupedToday.oggi.map(t => (
                  <TaskCard key={t.id} task={t} prop={propById(t.property_id)} room={roomById(t.room_id)} justDone={recentDoneRef.current.has(t.id)} onOpenReport={setReportPreview} onConfigure={setConfigureTask} />
                ))}
              </Section>
            )}
            {groupedToday.domani.length > 0 && (
              <Section title="Domani" tone="blue" count={groupedToday.domani.length}>
                {groupedToday.domani.map(t => (
                  <TaskCard key={t.id} task={t} prop={propById(t.property_id)} room={roomById(t.room_id)} justDone={recentDoneRef.current.has(t.id)} onOpenReport={setReportPreview} onConfigure={setConfigureTask} />
                ))}
              </Section>
            )}
            {/* Prenotazioni passate — nascoste dentro dropdown */}
            {pastTasks.length > 0 && (
              <details className="mt-4 bg-white border border-zinc-200" data-testid="past-tasks-details">
                <summary className="px-4 py-3 cursor-pointer list-none flex items-center justify-between hover:bg-zinc-50">
                  <span className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-500">
                    Prenotazioni passate
                  </span>
                  <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5">
                    {pastTasks.length} in ritardo
                  </span>
                </summary>
                <div className="px-3 pb-3 pt-1 space-y-2 border-t border-zinc-100">
                  {pastTasks.map(t => (
                    <TaskCard key={t.id} task={t} prop={propById(t.property_id)} room={roomById(t.room_id)} justDone={recentDoneRef.current.has(t.id)} onOpenReport={setReportPreview} onConfigure={setConfigureTask} />
                  ))}
                </div>
              </details>
            )}
          </>
        )}

        {!loading && tab === 'upcoming' && (
          upcomingTasks.length === 0 ? (
            <EmptyState icon={Sparkle} title="Nessun turno in arrivo" body="Sincronizza gli iCal o aggiungi una prenotazione manuale dalla Gestione Pulizie." />
          ) : (
            <Section title="Prossimi turni" tone="blue" count={upcomingTasks.length}>
              {upcomingTasks.map(t => (
                <TaskCard key={t.id} task={t} prop={propById(t.property_id)} room={roomById(t.room_id)} justDone={recentDoneRef.current.has(t.id)} onOpenReport={setReportPreview} onConfigure={setConfigureTask} compact />
              ))}
            </Section>
          )
        )}

        {!loading && tab === 'reports' && (
          reports.length === 0 ? (
            <EmptyState icon={Bell} title="Nessuna segnalazione" body="Quando lo staff lascerà una segnalazione, riceverai una notifica email e la vedrai qui." />
          ) : (
            <div className="space-y-2">
              {reports.map(r => (
                <ReportCard key={r.id} report={r} prop={properties.find(p => (p.id || p._id) === r.property_id)} room={rooms.find(rm => rm.id === r.room_id)} onOpen={() => setReportPreview(r.id)} />
              ))}
            </div>
          )
        )}

        {/* Manage link */}
        <div className="mt-8 mb-4 text-center">
          <button
            onClick={() => navigate('/pulizie')}
            className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-zinc-500 hover:text-zinc-900 underline underline-offset-4"
            data-testid="open-full-cleaning"
          >
            Apri Gestione Pulizie completa →
          </button>
        </div>
      </main>

      {/* Floating action button: nuova pulizia */}
      <button
        onClick={() => setShowManualDialog(true)}
        className="fixed bottom-5 right-5 z-40 w-14 h-14 bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xl shadow-emerald-600/40 flex items-center justify-center transition-all hover:scale-105 active:scale-95 rounded-full"
        aria-label="Nuova pulizia"
        data-testid="fab-add-cleaning"
      >
        <Plus size={26} weight="bold" />
      </button>

      {/* Manual task dialog */}
      {showManualDialog && (
        <ManualTaskDialog
          draft={manualDraft}
          setDraft={setManualDraft}
          properties={propsWithCleaning}
          rooms={rooms}
          propById={propById}
          creating={creating}
          onClose={() => setShowManualDialog(false)}
          onCreate={onCreateManual}
        />
      )}

      {/* Configure task dialog */}
      {configureTask && (
        <ConfigureTaskDialog
          task={configureTask}
          prop={propById(configureTask.property_id)}
          rooms={rooms}
          onClose={() => setConfigureTask(null)}
          onSaved={(updated) => {
            setTasks(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));
          }}
        />
      )}

      {/* Report preview modal */}
      {reportPreview && <ReportPreviewModal id={reportPreview} onClose={() => setReportPreview(null)} />}
    </div>
  );
}

function KPI({ label, value, sub, color }) {
  const colorMap = {
    emerald: 'text-emerald-700 bg-emerald-50',
    blue: 'text-blue-700 bg-blue-50',
    rose: 'text-rose-700 bg-rose-50',
  };
  return (
    <div className={`p-3 border border-zinc-200 ${colorMap[color] || 'bg-white'}`} data-testid={`kpi-${color}`}>
      <p className="text-[9px] font-black uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-2xl font-black tabular-nums leading-none mt-1">{value}</p>
      <p className="text-[10px] opacity-70 mt-1">{sub}</p>
    </div>
  );
}

function Section({ title, tone, count, children }) {
  const toneMap = {
    rose: 'text-rose-700',
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
  };
  return (
    <section className="mb-5">
      <div className="flex items-center justify-between mb-2 px-1">
        <p className={`text-[11px] font-black uppercase tracking-[0.18em] ${toneMap[tone] || 'text-zinc-700'}`}>{title}</p>
        <span className="text-[10px] font-bold text-zinc-400">{count}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="bg-white border border-zinc-200 p-8 text-center" data-testid="empty-state">
      <Icon size={36} weight="light" className="mx-auto text-zinc-300 mb-2" />
      <p className="text-sm font-bold text-zinc-900">{title}</p>
      <p className="text-xs text-zinc-500 mt-1">{body}</p>
    </div>
  );
}

function TaskCard({ task, prop, room, justDone, onOpenReport, onConfigure, compact }) {
  const isDone = task.status === 'fatto';
  const hasReport = !!task.report_id;
  const isStanze = prop?.cleaning_type === 'stanze';
  const needsRoom = isStanze && !task.room_id;
  const needsGuests = !task.num_guests;
  const needsConfig = !isDone && (needsRoom || needsGuests);
  const extras = task.extras || [];

  const extraLabels = {
    culla: '🍼 Culla',
    lettino_viaggio: '🧳 Lettino viaggio',
    letti_divisi: '✂️ Letti divisi',
  };

  return (
    <div
      className={`bg-white border-l-4 ${isDone ? 'border-emerald-500 bg-emerald-50/30' : hasReport ? 'border-rose-500' : 'border-zinc-300'} p-3 ${justDone ? 'ring-2 ring-emerald-500 animate-pulse' : ''}`}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            {isStanze ? <Bed size={11} weight="bold" className="text-zinc-400" /> : <House size={11} weight="bold" className="text-zinc-400" />}
            <p className="text-sm font-bold text-zinc-900 truncate">{room?.name || prop?.name || '—'}</p>
            {isDone && (
              <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-emerald-600 text-white inline-flex items-center gap-0.5">
                <CheckCircle size={9} weight="fill" /> Fatto
              </span>
            )}
            {hasReport && (
              <button
                onClick={() => onOpenReport(task.report_id)}
                className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-rose-100 text-rose-700 inline-flex items-center gap-0.5 hover:bg-rose-200"
                data-testid={`open-report-${task.id}`}
              >
                <Warning size={9} weight="fill" /> Segnalazione
              </button>
            )}
          </div>
          {isStanze && (
            <p className="text-[11px] text-zinc-500">{prop?.name}</p>
          )}
          <p className="text-[11px] text-zinc-500">
            Check-out: <strong className="text-zinc-700">{fmtDate(task.check_out)}</strong>
            {task.num_guests ? ` · ${task.num_guests} ospiti` : ''}
            {task.platform ? ` · ${task.platform}` : ''}
          </p>
          {extras.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {extras.map(ex => (
                <span key={ex} className="text-[10px] font-bold px-1.5 py-0.5 bg-violet-50 text-violet-700 border border-violet-200">{extraLabels[ex] || ex}</span>
              ))}
            </div>
          )}
          {!compact && task.guest_name && (
            <p className="text-[10px] text-zinc-400 mt-0.5">👤 {task.guest_name}</p>
          )}
        </div>
        {!isDone && onConfigure && (
          <button
            onClick={() => onConfigure(task)}
            className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider transition-all active:scale-95 ${
              needsConfig
                ? 'bg-amber-500 hover:bg-amber-600 text-white animate-pulse-slow'
                : 'bg-white border-2 border-zinc-300 hover:border-zinc-900 text-zinc-700'
            }`}
            data-testid={`configure-task-card-${task.id}`}
          >
            <Gear size={11} weight="bold" />
            {needsConfig ? 'Configura' : 'Modifica'}
          </button>
        )}
      </div>
    </div>
  );
}

function ReportCard({ report, prop, room, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="w-full text-left bg-white border border-rose-200 hover:border-rose-400 p-3 transition-colors"
      data-testid={`report-card-${report.id}`}
    >
      <div className="flex items-start gap-2">
        <Warning size={16} weight="fill" className="text-rose-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-zinc-900 truncate">{room?.name || prop?.name || 'Struttura'}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {(report.categories || []).map(c => (
              <span key={c} className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-rose-100 text-rose-800">{c}</span>
            ))}
          </div>
          {report.text && <p className="text-[11px] text-zinc-600 mt-1.5 line-clamp-2">{report.text}</p>}
          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-zinc-400">
            {(report.photos_count || 0) > 0 && <span className="inline-flex items-center gap-0.5"><Camera size={10} weight="bold" /> {report.photos_count}</span>}
            <span>{report.created_at?.slice(0, 10)}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function ReportPreviewModal({ id, onClose }) {
  const [report, setReport] = useState(null);
  useEffect(() => {
    axios.get(`${API}/cleaning/reports/${id}`).then(r => setReport(r.data)).catch(() => setReport(null));
  }, [id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="report-modal">
      <div className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-none">
        <div className="sticky top-0 px-5 py-4 border-b border-zinc-200 flex items-center justify-between bg-white">
          <h3 className="text-sm font-black uppercase tracking-wider text-rose-700 inline-flex items-center gap-1.5">
            <Warning size={14} weight="fill" /> Segnalazione
          </h3>
          <button onClick={onClose} className="p-1" data-testid="close-report-modal"><X size={18} /></button>
        </div>
        <div className="p-5">
          {!report && <p className="text-sm text-zinc-500">Caricamento…</p>}
          {report && (
            <>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(report.categories || []).map(c => (
                  <span key={c} className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-rose-100 text-rose-800 border border-rose-200">{c}</span>
                ))}
              </div>
              {report.text && (
                <p className="text-sm text-zinc-700 mb-3 leading-relaxed bg-zinc-50 p-3 border-l-4 border-zinc-300 whitespace-pre-wrap">{report.text}</p>
              )}
              {(report.photos || []).length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {report.photos.map((p, i) => (
                    <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="block aspect-square border border-zinc-200 hover:border-zinc-900">
                      <img src={p} alt="" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-zinc-400 mt-3">Inviata il {report.created_at?.slice(0, 10)}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function ManualTaskDialog({ draft, setDraft, properties, rooms, propById, creating, onClose, onCreate }) {
  const selectedProp = draft.property_id ? propById(draft.property_id) : null;
  const needsRoom = selectedProp?.cleaning_type === 'stanze';
  const filteredRooms = needsRoom ? rooms.filter(r => r.property_id === draft.property_id) : [];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="manual-task-dialog">
      <div className="bg-white w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-none animate-in slide-in-from-bottom duration-200">
        <div className="sticky top-0 px-5 py-4 border-b border-zinc-200 flex items-center justify-between bg-white">
          <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 inline-flex items-center gap-1.5">
            <Plus size={14} weight="bold" className="text-emerald-600" /> Nuova pulizia
          </h3>
          <button onClick={onClose} className="p-1" data-testid="close-manual-dialog" aria-label="Chiudi"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {properties.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-zinc-500 mb-2">Nessuna struttura configurata per le pulizie.</p>
              <p className="text-[11px] text-zinc-400">Apri "Gestione Pulizie" e imposta il tipo (Appartamento o Stanze) sulle tue strutture.</p>
            </div>
          ) : (
            <>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Struttura</label>
                <select
                  value={draft.property_id}
                  onChange={(e) => setDraft({ ...draft, property_id: e.target.value, room_id: '' })}
                  className="w-full h-10 px-3 border border-zinc-300 text-sm focus:outline-none focus:border-emerald-600"
                  data-testid="manual-prop-select"
                >
                  <option value="">— seleziona —</option>
                  {properties.map(p => (
                    <option key={p.id || p._id} value={p.id || p._id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {needsRoom && (
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Stanza</label>
                  <select
                    value={draft.room_id}
                    onChange={(e) => setDraft({ ...draft, room_id: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-300 text-sm focus:outline-none focus:border-emerald-600"
                    data-testid="manual-room-select"
                  >
                    <option value="">— seleziona —</option>
                    {filteredRooms.map(r => (
                      <option key={r.id} value={r.id}>{r.name} (max {r.max_occupancy})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Check-in</label>
                  <input
                    type="date"
                    value={draft.check_in}
                    onChange={(e) => setDraft({ ...draft, check_in: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-300 text-sm focus:outline-none focus:border-emerald-600"
                    data-testid="manual-checkin"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Check-out</label>
                  <input
                    type="date"
                    value={draft.check_out}
                    onChange={(e) => setDraft({ ...draft, check_out: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-300 text-sm focus:outline-none focus:border-emerald-600"
                    data-testid="manual-checkout"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">N° ospiti</label>
                <input
                  type="number"
                  min="1"
                  value={draft.num_guests}
                  onChange={(e) => setDraft({ ...draft, num_guests: Number(e.target.value) })}
                  className="w-full h-10 px-3 border border-zinc-300 text-sm focus:outline-none focus:border-emerald-600"
                  data-testid="manual-guests"
                />
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Note staff (opzionale)</label>
                <textarea
                  rows={2}
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  className="w-full border border-zinc-300 p-2.5 text-sm resize-none focus:outline-none focus:border-emerald-600"
                  placeholder="Es. cambio lenzuola tutte le stanze"
                  data-testid="manual-notes"
                />
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 px-5 py-3 border-t border-zinc-200 flex gap-2 bg-white">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-zinc-300 text-xs font-black uppercase tracking-wider text-zinc-700 hover:bg-zinc-50"
            data-testid="manual-cancel"
          >
            Annulla
          </button>
          <button
            onClick={onCreate}
            disabled={creating || properties.length === 0}
            className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="manual-create"
          >
            {creating ? 'Salvataggio…' : 'Crea pulizia'}
          </button>
        </div>
      </div>
    </div>
  );
}
